import { Box, Button, Divider, IconButton, OutlinedInput, Stack, Typography } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IconApple } from "src/components/icons-mui/social/apple";
import { IconFacebook } from "src/components/icons-mui/social/facebook";
import { IconGoogle } from "src/components/icons-mui/social/google";
import { getGlobals } from "src/consts/globals";
import { createThirdPartyAuthUrl } from "src/consts/urls";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { useAuthModalStore } from "src/stores/auth-modal-store";
import { EnumProviders } from "src/types/layout";

const PROVIDER_CONFIG = {
    [EnumProviders.Apple]: {
        labelLogin: "login-with-apple",
        icon: <IconApple />,
    },
    [EnumProviders.Facebook]: {
        labelLogin: "login-with-fb",
        icon: <IconFacebook />,
    },
    [EnumProviders.Google]: {
        labelLogin: "login-with-google",
        icon: <IconGoogle />,
    },
};

type ThirdPartyLoginButtonProps = {
    label: string;
    icon: JSX.Element;
    url: string;
};

function ThirdPartyLoginButton({ label, icon, url }: ThirdPartyLoginButtonProps) {
    const { t } = useTranslation("common");
    return (
        <IconButton component="a" aria-label={t(label)} key={label} variant="outlined" href={url} sx={{ backgroundColor: "white" }} size="large">
            {icon}
        </IconButton>
    );
}

type ThirdPartyButtonsProps = {
    providers: EnumProviders[];
};

function ThirdPartyButtons({ providers }: ThirdPartyButtonsProps) {
    const { returnUrl } = useAuthModalStore();
    return (
        <>
            {providers
                .filter((provider: EnumProviders) => PROVIDER_CONFIG[provider])
                .map((provider: EnumProviders) => {
                    const { icon, labelLogin } = PROVIDER_CONFIG[provider];
                    const url = createThirdPartyAuthUrl(provider, returnUrl);
                    return <ThirdPartyLoginButton key={labelLogin} label={labelLogin} icon={icon} url={url} />;
                })}
        </>
    );
}

export type HeroSlideRegisterBoxProps = {
    registerButtonColor?: "green" | "gray";
};

export const HeroSlideRegisterBox = ({ registerButtonColor = "green" }: HeroSlideRegisterBoxProps) => {
    const { t } = useTranslation(["common", "hero-slider-module"]);
    const { buildRegisterModalLink } = useLinkBuilder();
    const { prefilledRegisterEmail, setPrefilledRegisterEmail, setShowEmailRegisterForm } = useAuthModalStore();
    const globals = getGlobals();
    const loginProviders = globals?.loginProviders ?? [];

    return (
        <Stack spacing={2}>
            <OutlinedInput
                type="email"
                placeholder={t("email")}
                aria-label={t("email")}
                value={prefilledRegisterEmail}
                onChange={e => setPrefilledRegisterEmail(e.target.value)}
            />

            <Button
                color={registerButtonColor}
                fullWidth
                component="a"
                href={buildRegisterModalLink()}
                onClick={() => {
                    setShowEmailRegisterForm(true);
                }}
            >
                {t("register-for-free")}
            </Button>

            {loginProviders.length > 1 && (
                <Divider sx={{ borderColor: "gray.600" }}>
                    <Typography component="span" color="gray.800" paddingX="6px">
                        {t("buttons.register-via", { ns: "hero-slider-module" })}
                    </Typography>
                </Divider>
            )}

            <Box display="flex" justifyContent="center" gap={2}>
                <ThirdPartyButtons providers={loginProviders} />
            </Box>
        </Stack>
    );
};
